// ============================================================
// drive_mode_config.js
// EV Powertrain Simulator — 드라이브 모드 설정 단일 출처 (SST)
//
// 이 파일이 드라이브 모드 관련 모든 정의의 유일한 출처입니다.
// 신규 모드 추가 · 게인 수정 · 회생제동 강도 변경 → 이 파일 1곳만 수정.
//
// 사용처:
//   motor_control.js  → DRIVE_MODE_GAIN_TABLE (FOC 제어기 게인 배율)
//   drive_control.js  → DRIVE_MODE_REGEN (회생제동 강도 기본값)
//   simulation.js     → DRIVE_MODE_GAIN_TABLE_SIM 인라인 사본 (zero-import 제약)
//                        ★ 게인 수정 시 simulation.js §DRIVE_MODE_GAINS_INLINE도 함께 수정
// ============================================================

// ── FOC 제어기 게인 배율 테이블 ─────────────────────────────────────────────
//
// 물리적 근거:
//  ECO      : 전류 루프 BW ↓ → 스위칭 손실·철손 감소, 소프트 토크 응답
//             속도 루프 BW ↓ → 부드러운 가속, 타이어 슬립 마진 확보
//  NORMAL   : 기준값 (차량 프리셋 Kp/Ki 그대로)
//  SPORT    : 전류 루프 BW ↑ → 빠른 토크 응답
//             Ki 증분은 Kp보다 약간 적게 (위상 여유 유지)
//  SNOW     : 전류 루프 BW ↓↓ → 저μ 노면 급격한 토크 변동 억제, 스핀 방지
//             Ki 감소폭 > Kp 감소폭 → 적분 와인드업 위험 최소화
//  OFF_ROAD : 전류 루프 BW ↓ → 험로 충격 전류 스파이크 완화
//             SNOW보다 높게 설정 (OFF_ROAD는 더 적극적 제어 요구)
//
// 각 계수는 NORMAL 기준값에 대한 배율 (multiplier).
// motor_control.js의 applyDriveModeGains()에서 이 테이블을 참조하여
// 실제 Kp·Ki 값을 NORMAL 기준값 × 배율로 계산한다.
/**
 * 드라이브 모드별 FOC 제어기 게인 배율 테이블 (Single Source of Truth).
 *
 * 각 항목은 NORMAL 모드 기준 게인에 대한 배율(multiplier)이며,
 * motor_control.js의 applyDriveModeGains()에서 참조된다.
 * 모드 추가나 게인 수정은 반드시 이 파일 1곳에서만 수행해야 한다.
 *
 * 배율 설계 원칙:
 *   전류 루프 Kp 배율 = 대역폭 배율 (Kp/L ≈ ωc).
 *   Ki 배율은 Kp 배율보다 약간 낮게 설정하여 PI 영점(Ki/Kp)을 고주파 쪽으로 유지,
 *   위상 여유 감소를 억제한다.
 *   속도 루프 Kp/Ki 배율은 전류 루프 배율보다 낮게 설정하여 BW 분리비(≥5)를 유지한다.
 *
 * @type {Record<string, { Kp_id, Ki_id, Kp_iq, Ki_iq, Kp_spd, Ki_spd,
 *                          label, labelKo, color, desc, descEn, bw_factor }>}
 */
export const DRIVE_MODE_GAIN_TABLE = {
  ECO: {
    // 전류 제어기 (d-axis)
    Kp_id: 0.65,
    Ki_id: 0.55,
    // 전류 제어기 (q-axis)
    Kp_iq: 0.65,
    Ki_iq: 0.55,
    // 속도 제어기
    Kp_spd: 0.60,
    Ki_spd: 0.50,
    // 설명 메타
    label: 'ECO',
    labelKo: '에코',
    color: '#22c55e',
    desc: '낮은 BW → 손실↓, 부드러운 응답',
    descEn: 'Low BW → reduced losses, smooth torque',
    bw_factor: 0.65,
  },
  NORMAL: {
    Kp_id: 1.00,
    Ki_id: 1.00,
    Kp_iq: 1.00,
    Ki_iq: 1.00,
    Kp_spd: 1.00,
    Ki_spd: 1.00,
    label: 'NORMAL',
    labelKo: '노멀',
    color: '#60a5fa',
    desc: '기준 게인 (프리셋 값)',
    descEn: 'Baseline gains (preset values)',
    bw_factor: 1.00,
  },
  SPORT: {
    Kp_id: 1.45,
    Ki_id: 1.30,
    Kp_iq: 1.45,
    Ki_iq: 1.30,
    Kp_spd: 1.50,
    Ki_spd: 1.40,
    label: 'SPORT',
    labelKo: '스포츠',
    color: '#f97316',
    desc: '높은 BW → 즉각 토크 응답, PM 마진 확인 필수',
    descEn: 'High BW → instant torque response (verify PM margin)',
    bw_factor: 1.45,
  },
  SNOW: {
    // 전류 루프: BW를 50% 수준으로 대폭 낮춤
    //   → 저μ 노면(μ≈0.4)에서 급격한 토크 변동 억제, 구동 슬립 방지
    //   → Ki 감소폭이 Kp보다 큼: 적분 와인드업 위험 최소화
    Kp_id: 0.50,
    Ki_id: 0.40,
    Kp_iq: 0.50,
    Ki_iq: 0.40,
    // 속도 루프: ECO보다 훨씬 완만
    //   → 속도 오차에 과민 반응 금지 (스핀 유발 방지)
    //   → tau_torque_snow=120ms 설정과 일관성 유지
    Kp_spd: 0.45,
    Ki_spd: 0.35,
    label: 'SNOW',
    labelKo: '스노우',
    color: '#38bdf8',
    desc: '저μ 노면 → 토크 변동 최소화, 스핀 방지 (τ=120ms 일관)',
    descEn: 'Low-μ road → minimize torque transients, prevent wheel spin (τ=120ms)',
    bw_factor: 0.50,
  },
  OFF_ROAD: {
    // 전류 루프: NORMAL보다 낮게, ECO보다는 높게
    //   → 험로 충격(돌출 지면, 크로스 악슬)에 의한 전류 스파이크 완화
    //   → 토크벡터링(tv_yaw_gain_offroad=600) 응답 안정화를 위해 과도한 BW 억제
    Kp_id: 0.80,
    Ki_id: 0.70,
    Kp_iq: 0.80,
    Ki_iq: 0.70,
    // 속도 루프: NORMAL보다 낮게, SNOW보다는 높게
    //   → 불균일 지면에서 속도 루프 진동 억제
    //   → tau_torque_offroad=60ms와 일관성 유지
    //   → 크롤링/락커 기능에 필요한 최소 응답속도 유지
    Kp_spd: 0.75,
    Ki_spd: 0.65,
    label: 'OFF_ROAD',
    labelKo: '오프로드',
    color: '#a855f7',
    desc: '험로 충격 완화 → 전류 스파이크↓, 속도 루프 안정화 (τ=60ms 일관)',
    descEn: 'Rough terrain → reduce current spikes, stabilize speed loop (τ=60ms)',
    bw_factor: 0.80,
  },
};

// ── 모드별 회생제동 강도 기본값 ─────────────────────────────────────────────
// drive_control.js → driveModeRegenStrength() 에서 참조
// params에 모드별 override가 있으면 우선 사용하고, 없으면 이 기본값 사용
/**
 * 드라이브 모드별 회생제동 강도 기본값 (Single Source of Truth).
 *
 * 각 함수는 params를 받아 사용자 정의값(override) 또는 기본값을 반환한다.
 * drive_control.js의 driveModeRegenStrength()에서 참조된다.
 *
 * 강도 범위: 0.0(회생 없음) ~ 1.0(최대 회생).
 *   ECO:      0.5 — 완만한 회생 (과도한 재생 회피 → 승차감)
 *   NORMAL:   0.7 — 표준 회생
 *   SPORT:    1.0 — 최대 회생 (원-페달 드라이빙 지원)
 *   SNOW:     0.3 — 저μ 노면에서 회생 토크가 차륜 잠금 유발 위험 → 최소화
 *   OFF_ROAD: 0.6 — 험로 크롤링 중 회생 토크 스파이크 완화
 *
 * @type {Record<string, (p: object) => number>}
 */
export const DRIVE_MODE_REGEN_DEFAULT = {
  ECO:      (p) => p.eco_regen_strength      ?? 0.5,
  NORMAL:   (p) => p.normal_regen_strength   ?? 0.7,
  SPORT:    (p) => p.sport_regen_strength    ?? 1.0,
  SNOW:     (p) => p.snow_regen_strength     ?? 0.3,
  OFF_ROAD: (p) => p.offroad_regen_strength  ?? 0.6,  // [A43-Fix-3] 1.0→0.6
};
